import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setValidationErrors } from '../../../../../../../../common/global/helpers';
import Button from '../../../../../../../../common/widgets/Button';
import Dialog from '../../../../../../../../common/widgets/Dialog';
import Dropdown from '../../../../../../../../common/widgets/Dropdown';
import Input from '../../../../../../../../common/widgets/Input';
import {
  AttendenceTypeModel,
  UpdateAttendenceTypeData,
} from '../../../../../../models/AttendenceType';
import { ErrorState, ErrorType } from '../../../../../../models/Errors';
import { PresenceTypeCategoryModel } from '../../../../../../models/TimeAndAttendance';
import { resources } from '../../../../../../resources/resources';
import { timesheetsAdminService } from '../../../../../../services/timesheets-admin';
import {
  Color,
  GetAttendanceTypeCategories,
  GetColors,
  colorItemRender,
  colorValueRender,
} from '../../types';

interface EditAttendenceTypeProps {
  attendanceType: AttendenceTypeModel;
  onSaveResponse: (isSaveSuccess: boolean) => void;
  onDeleteResponse: (isSaveSuccess: boolean) => void;
  onCloseAddDialog: () => void;
}

interface EditAttendenceTypeState {
  data: UpdateAttendenceTypeData;
  selectedColor: Color;
  selectedCategory: PresenceTypeCategoryModel;
  isSaving: boolean;
  isDeleting: boolean;
}

export default function EditAttendenceType(
  props: Readonly<EditAttendenceTypeProps>,
) {
  const { t: translate } = useTranslation();
  const { attendanceType, onSaveResponse, onDeleteResponse, onCloseAddDialog } =
    props;
  const colorList = GetColors();
  const categoryList = GetAttendanceTypeCategories();
  const [state, setState] = useState<EditAttendenceTypeState>({
    data: {
      atendenceTypeId: attendanceType.atendenceTypeId,
      typeEnDescription: attendanceType.typeEnDescription,
      typeRoDescription: attendanceType.typeRoDescription,
      shortNotation: attendanceType.shortNotation,
      hours: attendanceType.hours,
      bgColor: attendanceType.bgColor,
      fgColor: attendanceType.fgColor,
      categoryId: attendanceType.categoryId,
    },
    selectedColor: colorList.find(c => c.bgColor === attendanceType.bgColor),
    selectedCategory: categoryList.find(
      c => c.categoryId === attendanceType.categoryId,
    ),
  } as EditAttendenceTypeState);
  const [errors, setErrors] = useState<ErrorState>({});

  const handleSaveAttendanceTypeChanges = async () => {
    setState(
      produce(d => {
        d.isSaving = true;
      }),
    );
    const response = await timesheetsAdminService.updateAttendenceType(
      state.data,
    );
    if (!response.hasErrors) {
      onSaveResponse(true);
    } else if (response.errorType === ErrorType.ValidationError) {
      setValidationErrors(response, setErrors);
    } else {
      onSaveResponse(false);
    }
    setState(
      produce(d => {
        d.isSaving = false;
      }),
    );
  };

  const handleDeleteAttendanceType = async () => {
    setState(
      produce(d => {
        d.isDeleting = true;
      }),
    );
    const response = await timesheetsAdminService.deleteAttendenceType(
      state.data.atendenceTypeId,
    );
    if (!response.hasErrors) {
      onDeleteResponse(true);
    } else {
      onDeleteResponse(false);
    }
    setState(
      produce(d => {
        d.isSaving = false;
      }),
    );
  };

  const handleColorChanged = (e: DropDownListChangeEvent) => {
    setState(
      produce(d => {
        d.selectedColor = e.value;
        d.data.bgColor = e.value.bgColor;
        d.data.fgColor = e.value.fgColor;
      }),
    );
  };

  const handleAttendanceTypeCategoryChanged = (e: DropDownListChangeEvent) => {
    setState(
      produce(d => {
        d.selectedCategory = e.value;
        d.data.categoryId = e.value.categoryId;
      }),
    );
  };

  const clearErrors = (name: string) => {
    setErrors(
      produce(d => {
        if (d) {
          d[name] = [];
        }
      }),
    );
  };

  return (
    <Dialog
      closeDialog={onCloseAddDialog}
      showCloseIcon
      closeOnClickOutside={false}
      className="dialog-m attendence-settings-dialog"
    >
      <div className="dialog-content">
        <div className="dialog-title">
          {translate(resources.admin.timesheetsSettings.dialog.editTitle)}
        </div>
        <Input
          label={translate(
            resources.admin.timesheetsSettings.dialog.nameInEnglish,
          )}
          mandatory
          value={state.data.typeEnDescription}
          onChange={e => {
            setState(
              produce(d => {
                d.data.typeEnDescription = e.value;
              }),
            );
          }}
          errors={errors.typeEnDescription}
          clearErrors={() => clearErrors('typeEnDescription')}
        />

        <Input
          label={translate(
            resources.admin.timesheetsSettings.dialog.nameInRomanian,
          )}
          mandatory
          value={state.data.typeRoDescription}
          onChange={e => {
            setState(
              produce(d => {
                d.data.typeRoDescription = e.value;
              }),
            );
          }}
          errors={errors.typeRoDescription}
          clearErrors={() => clearErrors('typeRoDescription')}
        />
        <Input
          label={translate(
            resources.admin.timesheetsSettings.dialog.shortNotation,
          )}
          mandatory
          value={state.data.shortNotation}
          onChange={e =>
            setState(
              produce(d => {
                d.data.shortNotation = e.value;
              }),
            )
          }
          errors={errors.shortNotation}
          clearErrors={() => clearErrors('shortNotation')}
        />
        <Input
          type="number"
          label={translate(
            resources.admin.timesheetsSettings.dialog.hoursPerDay,
          )}
          value={state.data.hours}
          onChange={e =>
            setState(
              produce(d => {
                d.data.hours = Number(e.value);
              }),
            )
          }
          errors={errors.hours}
          clearErrors={() => clearErrors('hours')}
        />
        <Dropdown
          label={translate(
            resources.admin.timesheetsSettings.dialog.pickAColor,
          )}
          data={GetColors()}
          mandatory
          value={state.selectedColor}
          dataItemKey="id"
          textField="colorName"
          valueRender={colorValueRender}
          itemRender={colorItemRender}
          onChange={handleColorChanged}
        />
        <Dropdown
          label={translate(resources.admin.timesheetsSettings.dialog.category)}
          mandatory
          data={GetAttendanceTypeCategories()}
          value={state.selectedCategory}
          dataItemKey="categoryId"
          textField="description"
          onChange={handleAttendanceTypeCategoryChanged}
        />
      </div>

      <div className="dialog-actions horizontal">
        <Button
          className="btn btn-solid"
          onClick={handleSaveAttendanceTypeChanges}
          loading={state.isSaving}
        >
          {translate(resources.save)}
        </Button>
        <Button
          className="btn btn-outline"
          onClick={handleDeleteAttendanceType}
          loading={state.isDeleting}
        >
          {translate(resources.admin.timesheetsSettings.dialog.deleteThisType)}
        </Button>
      </div>
    </Dialog>
  );
}
